import { NATIVE_TOKEN_ADDRESS_PER_CHAIN_ID } from '@/constants/addresses';
import { CHAIN_IDS } from '@/constants/chain';

import type { DedaubScan } from '@/utils/0x/dedaub/types';
import { TokenResult } from '@/utils/0x/token-registry.types';
import {
  type TokenSecurity,
  binaryFlagToBool,
  getFirstCreator,
  getTransferFee,
  getTransferFeeRate,
  hasBalanceMutableAuthority,
} from '@/utils/0x/token-security.types';
import { isEthereumAddress } from '@/utils/models';
import { isZoraToken } from '@/utils/zora';

export type SecurityLevel = 'Neutral' | 'Risky' | 'Danger' | 'Pending' | 'Safe';

export interface AlertInfo {
  label: string;
  valueLabel: string;
  tooltipInfo: string | boolean;
  type: 'risk' | 'attention' | 'positive' | 'number' | 'address' | 'unknown' | 'hidden';
  isDedaubCheck?: boolean;
}

export interface SecurityAlertsResult {
  externalDependencies: AlertInfo[];
  accessControls: AlertInfo[];
  issuanceInformation: AlertInfo[];
  customBehaviors: AlertInfo[];
  previewAlerts: AlertInfo[];
}

export interface EvmSecurityScore {
  level: SecurityLevel;
  risks: number;
  highRisks: number;
}

export interface SolanaSecurityScore {
  level: SecurityLevel;
  risks: number;
}

// todo(MAT-4301): remove when token-api overrides native values
const isNativeEvmToken = (token: TokenResult) =>
  NATIVE_TOKEN_ADDRESS_PER_CHAIN_ID[token.chainId] === token.address &&
  token.chainId !== CHAIN_IDS.MANTLE;

export const getEvmSecurityScore = (
  activeToken: TokenResult,
  isListedOnCoinbaseOrBinance: boolean,
  isListedOnCoinGecko: boolean,
  includeDedaubScan = false,
  dedaubScan?: DedaubScan | null,
): EvmSecurityScore => {
  const hasAllowanceBypassRisk = includeDedaubScan && dedaubScan?.features.allowanceBypass === true;

  if (isNativeEvmToken(activeToken)) {
    return { level: 'Safe', risks: 0, highRisks: 0 };
  }
  if (isZoraToken(activeToken)) {
    return { level: 'Safe', risks: 0, highRisks: 0 };
  }

  const highRiskChecks = [
    hasAllowanceBypassRisk ? true : null,
    activeToken.isHoneypot,
    activeToken.isOpenSource === null ? null : !activeToken.isOpenSource,
  ];

  const riskChecks = [
    activeToken.isMintable,
    activeToken.isProxy,
    activeToken.slippageModifiable,
    /**
     * Todo: Goplus is showing incorrect buyTax values, so we are adding a temporary check here
     * to show the correct value in the preview section.
     * Remove this check once the issue is fixed.
     * See MAT:4601
     */
    activeToken.buyTax === null ? null : activeToken.buyTax > 0.001,
    activeToken.sellTax === null ? null : activeToken.sellTax !== 0,
    activeToken.antiWhaleModifiable,
    activeToken.canTakeBackOwnership,
    activeToken.cannotBuy,
    activeToken.cannotSellAll,
    activeToken.externalCall,
    activeToken.hiddenOwner,
    activeToken.isAntiWhale,
    activeToken.isBlacklisted,
    activeToken.isWhitelisted,
    activeToken.personalSlippageModifiable,
    activeToken.tradingCooldown,
    activeToken.transferPausable,
  ];

  const allRisksUnknown = [...highRiskChecks, ...riskChecks].every((value) => value === null);

  if (allRisksUnknown) {
    return { level: 'Pending', risks: 0, highRisks: 0 };
  }

  const highRisks = highRiskChecks.filter(Boolean).length;
  const risks = riskChecks.filter(Boolean).length;
  const isListed = activeToken.trustList || isListedOnCoinbaseOrBinance || isListedOnCoinGecko;

  let level: SecurityLevel = 'Neutral';
  if (highRisks > 0) {
    level = 'Danger';
  } else if (!isListed && risks > 0) {
    level = 'Risky';
  } else if (highRisks === 0 && risks === 0 && isListed) {
    level = 'Safe';
  }

  return { level, risks, highRisks };
};

/**
 * Classify a Solana token's security level from the token-api `/security`
 * shape. A null `trustedToken` alone no longer forces Pending: if token-api
 * returned a row, known risk signals still drive the score.
 */
export const getSolanaSecurityLevel = (
  securityInfo: TokenSecurity,
  isListedOnCoinbaseOrBinance: boolean,
  isListedOnCoinGecko: boolean,
): SolanaSecurityScore => {
  if (securityInfo.trustedToken === null) {
    return { level: 'Pending', risks: 0 };
  }

  const riskChecks = [
    securityInfo.isMintable === true,
    securityInfo.isFreezable === true,
    securityInfo.isClosable === true,
    securityInfo.nonTransferable === true,
    securityInfo.isMetadataMutable === true,
    hasBalanceMutableAuthority(securityInfo),
    getTransferFeeRate(securityInfo) > 0,
    getFirstCreator(securityInfo)?.malicious_address === 1,
    securityInfo.defaultAccountState === 'Frozen',
    securityInfo.isTransferHookUpgradable === true,
  ];
  const risks = riskChecks.filter(Boolean).length;
  const isListed = securityInfo.trustedToken || isListedOnCoinbaseOrBinance || isListedOnCoinGecko;

  let level: SecurityLevel = 'Neutral';
  if (!isListed && risks > 0) {
    level = 'Risky';
  } else if (risks === 0 && isListed) {
    level = 'Safe';
  }

  return { level, risks };
};

export const calculateSecurityAlerts = (
  activeToken: TokenResult,
  includeDedaubScan = false,
  dedaubScan?: DedaubScan | null,
): SecurityAlertsResult => {
  const isNativeToken = isNativeEvmToken(activeToken);

  const verifiedSourceCode: AlertInfo = {
    label: 'Verified Source Code',
    valueLabel:
      activeToken?.isOpenSource === null && !isNativeToken
        ? 'Unknown'
        : activeToken?.isOpenSource || isNativeToken
          ? 'Yes'
          : 'No',
    tooltipInfo:
      'Closed-sourced contracts often hide malicious behavior, are extremely risky, and should be avoided.',
    type:
      activeToken?.isOpenSource === null && !isNativeToken
        ? 'unknown'
        : activeToken?.isOpenSource || isNativeToken
          ? 'positive'
          : 'risk',
  };
  const externalCall: AlertInfo = {
    label: 'External Call',
    valueLabel:
      activeToken?.externalCall === null && !isNativeToken
        ? 'Unknown'
        : activeToken?.externalCall
          ? 'Yes'
          : 'No',
    tooltipInfo:
      'External calls indicate that this contract depends on other external contracts which may obscure additional risks.',
    type:
      activeToken?.externalCall === null && !isNativeToken
        ? 'unknown'
        : activeToken?.externalCall
          ? 'attention'
          : 'positive',
  };
  const proxyContract: AlertInfo = {
    label: 'Proxy Contract',
    valueLabel:
      activeToken?.isProxy === null && !isNativeToken
        ? 'Unknown'
        : activeToken?.isProxy
          ? 'Yes'
          : 'No',
    tooltipInfo:
      'Proxy contracts are paired with modifiable implementation contracts, which may obscure additional risks not covered here.',
    type:
      activeToken?.isProxy === null && !isNativeToken
        ? 'unknown'
        : activeToken?.isProxy
          ? 'attention'
          : 'positive',
  };
  const mintable: AlertInfo = {
    label: 'Mintable',
    valueLabel:
      activeToken?.isMintable === null && !isNativeToken
        ? 'Unknown'
        : activeToken?.isMintable
          ? 'Yes'
          : 'No',
    tooltipInfo:
      'Describes whether the token issuer can mint new tokens, increasing the supply. Make sure you trust the token issuer to not abuse this functionality.',
    type:
      activeToken?.isMintable === null && !isNativeToken
        ? 'unknown'
        : activeToken?.isMintable
          ? 'attention'
          : 'positive',
  };
  const pauseTransfers: AlertInfo = {
    label: 'Pause Transfers',
    valueLabel:
      activeToken?.transferPausable === null && !isNativeToken
        ? 'Unknown'
        : activeToken?.transferPausable
          ? 'Yes'
          : 'No',
    tooltipInfo: 'Indicates whether the contract creator can freeze token transfers or trades.',
    type:
      activeToken?.transferPausable === null && !isNativeToken
        ? 'unknown'
        : activeToken?.transferPausable
          ? 'attention'
          : 'positive',
  };
  const blacklist: AlertInfo = {
    label: 'Blacklist',
    valueLabel:
      activeToken?.isBlacklisted === null && !isNativeToken
        ? 'Unknown'
        : activeToken?.isBlacklisted
          ? 'Yes'
          : 'No',
    tooltipInfo: 'Indicates whether the contract creator can block specific addresses. ',
    type:
      activeToken?.isBlacklisted === null && !isNativeToken
        ? 'unknown'
        : activeToken?.isBlacklisted
          ? 'attention'
          : 'positive',
  };
  const whitelist: AlertInfo = {
    label: 'Whitelist',
    valueLabel:
      activeToken?.isWhitelisted === null && !isNativeToken
        ? 'Unknown'
        : activeToken?.isWhitelisted
          ? 'Yes'
          : 'No',
    tooltipInfo:
      'Indicates whether the contract creator can limit which addresses are allowed to hold the token.',
    type:
      activeToken?.isWhitelisted === null && !isNativeToken
        ? 'unknown'
        : activeToken?.isWhitelisted
          ? 'attention'
          : 'positive',
  };
  const creatorAddress: AlertInfo = {
    label: 'Creator Address',
    valueLabel:
      activeToken?.creatorAddress === null
        ? 'Unknown'
        : activeToken && activeToken.creatorAddress !== ''
          ? activeToken.creatorAddress
          : 'Unknown',
    tooltipInfo: 'The address used by the contract creator to deploy the token contract. ',
    type: activeToken?.creatorAddress === null ? 'unknown' : 'address',
  };
  const ownerAddress: AlertInfo = {
    label: 'Owner Address',
    valueLabel:
      activeToken?.ownerAddress === null
        ? 'Unknown'
        : activeToken && activeToken.ownerAddress !== ''
          ? activeToken.ownerAddress
          : 'Unknown',
    tooltipInfo:
      'The address with control over token parameters. Not all tokens have this functionality. Owner address may change over time.',
    type:
      activeToken?.ownerAddress === null || !isEthereumAddress(activeToken?.ownerAddress)
        ? 'unknown'
        : 'address',
  };
  const hiddenOwner: AlertInfo = {
    label: 'Hidden Owner',
    valueLabel:
      activeToken?.hiddenOwner === null && !isNativeToken
        ? 'Unknown'
        : activeToken?.hiddenOwner
          ? 'Yes'
          : 'No',
    tooltipInfo:
      'Indicates if the developer has secretly retained ownership even after abandoning ownership, often a signal of malicious intent.',
    type:
      activeToken?.hiddenOwner === null && !isNativeToken
        ? 'unknown'
        : activeToken?.hiddenOwner
          ? 'attention'
          : 'positive',
  };
  const retrievableOwnership: AlertInfo = {
    label: 'Retrievable Ownership',
    valueLabel:
      activeToken?.canTakeBackOwnership === null && !isNativeToken
        ? 'Unknown'
        : activeToken?.canTakeBackOwnership
          ? 'Yes'
          : 'No',
    tooltipInfo:
      'When ownership has been abandoned, the risks associated with contract modifications are mitigated. If ownership is retrievable, these risks may be reactivated.',
    type:
      activeToken?.canTakeBackOwnership === null && !isNativeToken
        ? 'unknown'
        : activeToken?.canTakeBackOwnership
          ? 'attention'
          : 'positive',
  };
  const cantBuy: AlertInfo = {
    label: "Can't Buy",
    valueLabel:
      activeToken?.cannotBuy === null && !isNativeToken
        ? 'Unknown'
        : activeToken?.cannotBuy
          ? 'Yes'
          : 'No',
    tooltipInfo: 'Indicates if buying has been deactivated.',
    type:
      activeToken?.cannotBuy === null && !isNativeToken
        ? 'unknown'
        : activeToken?.cannotBuy
          ? 'attention'
          : 'positive',
  };
  const cannotSellAll: AlertInfo = {
    label: "Can't Sell All",
    valueLabel:
      activeToken?.cannotSellAll === null && !isNativeToken
        ? 'Unknown'
        : activeToken?.cannotSellAll
          ? 'Yes'
          : 'No',
    tooltipInfo:
      'Indicates a restriction preventing you from selling all tokens at once. These tokens may require a specific amount to remain in the seller’s account.',
    type:
      activeToken?.cannotSellAll === null && !isNativeToken
        ? 'unknown'
        : activeToken?.cannotSellAll
          ? 'attention'
          : 'positive',
  };
  /**
   * Todo: Goplus is showing incorrect buyTax values, so we are adding a temporary check here
   * to show the correct value in the preview section.
   * Remove this check once the issue is fixed.
   * See MAT:4601
   */
  const buyTax: AlertInfo = {
    label: 'Buy Tax',
    valueLabel:
      activeToken?.buyTax === null && !isNativeToken
        ? 'Unknown'
        : (activeToken?.buyTax && activeToken.buyTax <= 0.001
            ? '0'
            : (activeToken?.buyTax || 0) * 100
          ).toString() + '%',
    tooltipInfo: 'An additional fee charged by the token creator on every Buy transaction.',
    type:
      activeToken?.buyTax === null && !isNativeToken
        ? 'unknown'
        : (activeToken.buyTax !== null && activeToken.buyTax <= 0.001) || isNativeToken
          ? 'positive'
          : 'attention',
  };
  const sellTax: AlertInfo = {
    label: 'Sell Tax',
    valueLabel:
      activeToken?.sellTax === null && !isNativeToken
        ? 'Unknown'
        : ((activeToken?.sellTax || 0) * 100).toString() + '%',
    tooltipInfo: 'An additional fee charged by the token creator on every Sell transaction.',
    type:
      activeToken?.sellTax === null && !isNativeToken
        ? 'unknown'
        : activeToken?.sellTax === 0 || isNativeToken
          ? 'positive'
          : (activeToken.sellTax !== null && activeToken.sellTax === 0) || isNativeToken
            ? 'positive'
            : 'attention',
  };
  const taxModifiable: AlertInfo = {
    label: 'Tax Modifiable',
    valueLabel:
      activeToken?.slippageModifiable === null && !isNativeToken
        ? 'Unknown'
        : activeToken?.slippageModifiable
          ? 'Yes'
          : 'No',
    tooltipInfo: 'Indicates whether a buy or sell tax can be added or modified by the issuer.',
    type:
      activeToken?.slippageModifiable === null && !isNativeToken
        ? 'unknown'
        : activeToken?.slippageModifiable
          ? 'attention'
          : 'positive',
  };
  const taxModifiableSingleAddress: AlertInfo = {
    label: 'Tax Modifiable (single address)',
    valueLabel:
      activeToken?.personalSlippageModifiable === null && !isNativeToken
        ? 'Unknown'
        : activeToken?.personalSlippageModifiable
          ? 'Yes'
          : 'No',
    tooltipInfo: 'Indicates whether the issuer can charge different tax rates across addresses.',
    type:
      activeToken?.personalSlippageModifiable === null && !isNativeToken
        ? 'unknown'
        : activeToken?.personalSlippageModifiable
          ? 'attention'
          : 'positive',
  };
  const tradingCooldown: AlertInfo = {
    label: 'Trading Cooldown',
    valueLabel:
      activeToken?.tradingCooldown === null && !isNativeToken
        ? 'Unknown'
        : activeToken?.tradingCooldown && !isNativeToken
          ? 'Yes'
          : 'No',
    tooltipInfo:
      'Indicates a rate limit that forces a minimum time delay between two transactions.',
    type:
      activeToken?.tradingCooldown === null && !isNativeToken
        ? 'unknown'
        : activeToken?.tradingCooldown
          ? 'attention'
          : 'positive',
  };
  const maximumBalance: AlertInfo = {
    label: 'Maximum Balance',
    valueLabel:
      activeToken?.isAntiWhale === null && !isNativeToken
        ? 'Unknown'
        : activeToken?.isAntiWhale
          ? 'Yes'
          : 'No',
    tooltipInfo:
      'Indicates if the contract has capped the amount of tokens that can be held by any single address. ',
    type:
      activeToken?.isAntiWhale === null && !isNativeToken
        ? 'unknown'
        : activeToken?.isAntiWhale
          ? 'attention'
          : 'positive',
  };
  const maxBalanceModifable: AlertInfo = {
    label: 'Max Balance Modifable',
    valueLabel:
      activeToken?.antiWhaleModifiable === null && !isNativeToken
        ? 'Unknown'
        : activeToken?.antiWhaleModifiable
          ? 'Yes'
          : 'No',
    tooltipInfo: 'Indicates if the contract can change the maximum balance.',
    type:
      activeToken?.antiWhaleModifiable === null && !isNativeToken
        ? 'unknown'
        : activeToken?.antiWhaleModifiable
          ? 'attention'
          : 'positive',
  };
  const honeypot: AlertInfo = {
    label: 'Honeypot',
    valueLabel:
      activeToken?.isHoneypot === null && !isNativeToken
        ? 'Unknown'
        : activeToken?.isHoneypot
          ? 'Yes'
          : 'No',
    tooltipInfo:
      'A honeypot contains malicious code that allows the token to be bought, but not sold. ',
    type:
      activeToken?.isHoneypot === null && !isNativeToken
        ? 'unknown'
        : activeToken?.isHoneypot
          ? 'risk'
          : 'positive',
  };
  const allowanceBypassResult = dedaubScan?.features.allowanceBypass;
  const allowanceBypass: AlertInfo = {
    label: 'Allowance Bypass',
    isDedaubCheck: true,
    valueLabel:
      allowanceBypassResult === true ? 'Yes' : allowanceBypassResult === false ? 'No' : 'Unknown',
    tooltipInfo:
      'Checks whether a privileged address can move tokens from holders without their approval.',
    type:
      allowanceBypassResult === true
        ? 'risk'
        : allowanceBypassResult === false
          ? 'positive'
          : 'unknown',
  };

  return {
    externalDependencies: [verifiedSourceCode, externalCall, proxyContract],
    accessControls: [
      mintable,
      pauseTransfers,
      blacklist,
      whitelist,
      ...(includeDedaubScan ? [allowanceBypass] : []),
    ],
    issuanceInformation: [creatorAddress, ownerAddress, hiddenOwner, retrievableOwnership],
    customBehaviors: [
      cantBuy,
      cannotSellAll,
      buyTax,
      sellTax,
      taxModifiable,
      taxModifiableSingleAddress,
      tradingCooldown,
      maximumBalance,
      maxBalanceModifable,
      honeypot,
    ],
    previewAlerts: [
      verifiedSourceCode,
      proxyContract,
      mintable,
      buyTax,
      sellTax,
      honeypot,
      ...(includeDedaubScan ? [allowanceBypass] : []),
    ],
  };
};

/** Build an AlertInfo from a three-state flag: true -> attention, false -> positive, null/undefined -> unknown. */
const buildBooleanAlert = (
  label: string,
  tooltipInfo: string,
  flag: boolean | null | undefined,
): AlertInfo => ({
  label,
  tooltipInfo,
  valueLabel: flag === true ? 'Yes' : flag === false ? 'No' : 'Unknown',
  type: flag === true ? 'attention' : flag === false ? 'positive' : 'unknown',
});

const DEFAULT_ACCOUNT_STATE_MAP: Record<string, { valueLabel: string; type: AlertInfo['type'] }> = {
  Uninitialized: { valueLabel: 'Uninitialized', type: 'attention' },
  Initialized: { valueLabel: 'Initialized', type: 'positive' },
  Frozen: { valueLabel: 'Frozen', type: 'risk' },
};
const UNKNOWN_STATE = { valueLabel: 'Unknown', type: 'unknown' as const };

export const calculateSolanaSecurityAlerts = (
  solanaSecurityData: TokenSecurity | undefined,
): SecurityAlertsResult => {
  const mintable = buildBooleanAlert(
    'Mintable',
    'Describes whether the token issuer can mint new tokens, increasing the supply. Make sure you trust the token issuer to not abuse this functionality.',
    solanaSecurityData?.isMintable,
  );

  const freezable = buildBooleanAlert(
    'Freezable',
    'Indicates whether the contract creator can prevent token transfers or trades.',
    solanaSecurityData?.isFreezable,
  );

  const closable = buildBooleanAlert(
    'Closable',
    'Indicates whether the contract creator can close the token’s SPL account, removing the asset from existence.',
    solanaSecurityData?.isClosable,
  );

  const transferFeeRate = getTransferFeeRate(solanaSecurityData);
  const hasKnownTransferFee = getTransferFee(solanaSecurityData)?.current_fee_rate !== undefined;
  const transferFee: AlertInfo = {
    label: 'Transfer Fee',
    tooltipInfo: 'A transfer fee is an additional tax charged on token transactions.',
    valueLabel:
      transferFeeRate > 0 ? `${transferFeeRate / 100}%` : hasKnownTransferFee ? 'No' : 'Unknown',
    type: transferFeeRate > 0 ? 'attention' : hasKnownTransferFee ? 'positive' : 'unknown',
  };

  const firstCreator = getFirstCreator(solanaSecurityData);
  const maliciousAddress = buildBooleanAlert(
    'Malicious Address',
    'Indicates whether the address is labelled as malicious.',
    binaryFlagToBool(firstCreator?.malicious_address),
  );

  const nonTransferable = buildBooleanAlert(
    'Non Transferable',
    'Indicates if token transfers are restricted. ',
    solanaSecurityData?.nonTransferable,
  );

  const metadataMutable = buildBooleanAlert(
    'Metadata Mutable',
    'Shows if metadata such as name and description can be altered.',
    solanaSecurityData?.isMetadataMutable,
  );

  // `null` column means unknown; otherwise defer to hasBalanceMutableAuthority.
  const balanceMutableFlag: boolean | null =
    solanaSecurityData?.balanceMutableAuthorityInfo == null
      ? null
      : hasBalanceMutableAuthority(solanaSecurityData);
  const balanceMutable = buildBooleanAlert(
    'Balance Mutable',
    'Shows if token balances can be altered by issuer.',
    balanceMutableFlag,
  );

  const creatorAddress: AlertInfo = {
    label: 'Creator Address',
    tooltipInfo: 'The address used by the contract creator to deploy the token contract.',
    valueLabel: firstCreator?.address ?? 'Unknown',
    type: firstCreator?.address ? 'address' : 'unknown',
  };

  const defaultAccountStateEntry =
    (solanaSecurityData?.defaultAccountState &&
      DEFAULT_ACCOUNT_STATE_MAP[solanaSecurityData.defaultAccountState]) ||
    UNKNOWN_STATE;
  const defaultAccountState: AlertInfo = {
    label: 'Default Account State',
    tooltipInfo:
      'If a token account is frozen by default, it must be unfrozen by the issuer before tokens can be used.',
    valueLabel: defaultAccountStateEntry.valueLabel,
    type: defaultAccountStateEntry.type,
  };

  const transferFeeUpgradeable = buildBooleanAlert(
    'Transfer Fee Upgradeable',
    'Detects if a Transfer Fee to collect tax on token transactions can be implemented or changed.',
    solanaSecurityData?.isTransferFeeUpgradable,
  );

  const transferHookUpgradeable = buildBooleanAlert(
    'Transfer Hook Upgradeable',
    'Detects if a Hook for executing custom logic when a token is transferred can be implemented or changed.',
    solanaSecurityData?.isTransferHookUpgradable,
  );

  return {
    previewAlerts: [mintable, freezable, closable, transferFee, maliciousAddress],
    accessControls: [
      mintable,
      freezable,
      closable,
      nonTransferable,
      metadataMutable,
      balanceMutable,
    ],
    issuanceInformation: [creatorAddress, defaultAccountState],
    customBehaviors: [transferFee, transferFeeUpgradeable, transferHookUpgradeable],
    externalDependencies: [],
  };
};
